import { cn } from '@antimatterhq/ui/lib/utils';
import { Skeleton } from '@antimatterhq/ui/components/skeleton';
import { useAtomValue } from 'jotai';
import { getHistogramBarInterval } from '../histogram.utils';
import { histogramVisibleTimeRangeAtom } from '../jotai/histogramAtoms';

type HistogramSkeletonBarsProps = {
  maxBarHeight?: number;
  className?: string;
};

const HistogramSkeletonBars = ({
  maxBarHeight = 40,
  className,
}: HistogramSkeletonBarsProps) => {
  const [startTime, endTime] = useAtomValue(histogramVisibleTimeRangeAtom);
  // Calculate the same interval as used in rendering (matching histogramBars.tsx logic)
  const visibleDurationMs =
    endTime.toDate().getTime() - startTime.toDate().getTime();
  const intervalMs = getHistogramBarInterval(visibleDurationMs);

  // Calculate number of skeleton bars to show based on actual interval
  const numberOfSkeletons = Math.ceil(visibleDurationMs / intervalMs);

  // Match the unloaded bar height (75% of maxBarHeight)
  const skeletonHeight = maxBarHeight * 0.75;

  // Calculate the exact width each bar should take
  const barWidthPercentage = 100 / numberOfSkeletons;

  return (
    <div
      className={cn('relative w-full overflow-hidden', className)}
      style={{ height: maxBarHeight }}
    >
      <div className="absolute inset-0 flex" style={{ width: '100%' }}>
        {Array.from({ length: numberOfSkeletons }).map((_, index) => {
          return (
            <div
              key={index}
              className="relative"
              style={{
                width: `${barWidthPercentage}%`,
                height: '100%',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: '0',
                  padding: '0 1px',
                  display: 'flex',
                  alignItems: 'flex-end',
                }}
              >
                <Skeleton
                  className="w-full rounded-none"
                  style={{
                    height: `${skeletonHeight}px`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { HistogramSkeletonBars };
